import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { supabaseCommunityService } from '../../../../services/supabase/communities';
import type { Community } from '../../../../types/community';
import type { Project } from '../../../../types/project';
import type { Supplier } from '../../../../types/supplier';

interface AddExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

// Define the form data interface
interface FormData {
  community: string;
  date: string;
  expenseAmount: string;
  description: string;
  expenseProject: string;
  acquisitionDate: string;
  supplier: string;
}

export default function AddExpenseModal({
  isOpen,
  onClose,
  onSuccess
}: AddExpenseModalProps) {
  // Initialize form with sensible defaults
  const getInitialFormData = (): FormData => ({
    community: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    expenseAmount: '',
    description: '',
    expenseProject: '',
    acquisitionDate: '',
    supplier: ''
  });

  // State management
  const [formData, setFormData] = useState<FormData>(getInitialFormData());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch communities
  const { 
    data: communities, 
    isLoading: isLoadingCommunities, 
    error: communitiesError 
  } = useQuery<Community[]>({
    queryKey: ['communities'],
    queryFn: supabaseCommunityService.getAll,
    enabled: isOpen
  });

  // Fetch projects
  const {
    data: projects,
    isLoading: isLoadingProjects,
    error: projectsError
  } = useQuery<Project[]>({
    queryKey: ['projects'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('proj_id');

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen
  });

  // Fetch suppliers
  const {
    data: suppliers,
    isLoading: isLoadingSuppliers,
    error: suppliersError
  } = useQuery<Supplier[]>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('suppl_abrev');

      if (error) throw error;
      return data || [];
    },
    enabled: isOpen
  });

  // Debug logging
  useEffect(() => {
    if (communitiesError) {
      console.error('Error fetching communities:', communitiesError);
    }
    if (projectsError) {
      console.error('Error fetching projects:', projectsError);
    }
    if (suppliersError) {
      console.error('Error fetching suppliers:', suppliersError);
    }
  }, [communitiesError, projectsError, suppliersError]);

  // Reset form state
  const resetForm = () => {
    setFormData(getInitialFormData());
    setError(null);
    setIsSubmitting(false);
  };
  
  // Wrap the original onClose to reset form state
  const handleClose = () => {
    resetForm();
    onClose();
  };

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Create a timeout promise to prevent hanging
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Request timed out after 10 seconds')), 10000);
    });

    try {
      // Process form data
      const processedData = {
        fin_comm: formData.community,
        fin_date: formData.date || null,
        fin_inc_amt: null, // No income amount for expense records
        fin_exp_amt: formData.expenseAmount ? parseFloat(formData.expenseAmount) : null,
        fin_desc: formData.description || null,
        fin_exp_proj: formData.expenseProject || null,
        fin_acq_date: formData.acquisitionDate || null,
        fin_supplier: formData.supplier || null
      };
      
      console.log('Creating expense record with data:', processedData);
      
      // Submit data
      const { data, error: insertError } = await Promise.race([
        supabase
          .from('dev_financials')
          .insert([processedData])
          .select('*')
          .single(),
        timeoutPromise
      ]) as any;

      if (insertError) throw insertError;
      
      // Reset form and close modal
      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error adding expense record:', err);
      setError(`Failed to add expense record: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-red-700">Add Expense Record</h2>
            <button
              onClick={handleClose}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Close"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Community */}
              <div>
                <label htmlFor="community" className="block text-sm font-medium text-gray-700 mb-1">
                  Community *
                </label>
                {isLoadingCommunities && (
                  <div className="mt-1 text-sm text-gray-500">Loading communities...</div>
                )}
                {communitiesError && (
                  <div className="mt-1 text-sm text-red-500">
                    Error loading communities
                  </div>
                )}
                <div className="relative">
                  <select
                    id="community"
                    name="community"
                    value={formData.community}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    required
                    disabled={isLoadingCommunities}
                  >
                    <option value="">Select a Community</option>
                    {communities?.map((community) => (
                      <option 
                        key={community.comm_id} 
                        value={community.comm_id}
                        className="py-1"
                        title={community.comm_name}
                      >
                        {community.comm_id}
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">Community name</p>
              </div>

              {/* Date */}
              <div>
                <label htmlFor="date" className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Transaction date</p>
              </div>

              {/* Expense Amount */}
              <div>
                <label htmlFor="expenseAmount" className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  id="expenseAmount"
                  name="expenseAmount"
                  value={formData.expenseAmount}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                  placeholder="Enter expense amount"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Expense amount in dollars</p>
              </div>

              {/* Supplier */}
              <div>
                <label htmlFor="supplier" className="block text-sm font-medium text-gray-700 mb-1">
                  Supplier
                </label>
                {isLoadingSuppliers && (
                  <div className="mt-1 text-sm text-gray-500">Loading suppliers...</div>
                )}
                {suppliersError && (
                  <div className="mt-1 text-sm text-red-500">
                    Error loading suppliers
                  </div>
                )}
                <div className="relative">
                  <select
                    id="supplier"
                    name="supplier"
                    value={formData.supplier}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    disabled={isLoadingSuppliers}
                  >
                    <option value="">Select a Supplier</option>
                    {suppliers?.map((supplier) => (
                      <option 
                        key={supplier.suppl_abrev} 
                        value={supplier.suppl_abrev}
                        className="py-1"
                      >
                        {supplier.supplier_name} ({supplier.suppl_abrev})
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">Supplier or vendor name</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                placeholder="Enter description"
                maxLength={200}
              />
              <p className="mt-1 text-xs text-gray-500">
                {formData.description.length}/200 characters - Description of the expense transaction
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Expense Project */}
              <div>
                <label htmlFor="expenseProject" className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Project
                </label>
                {isLoadingProjects && (
                  <div className="mt-1 text-sm text-gray-500">Loading projects...</div>
                )}
                {projectsError && (
                  <div className="mt-1 text-sm text-red-500">
                    Error loading projects
                  </div>
                )}
                <div className="relative">
                  <select
                    id="expenseProject"
                    name="expenseProject"
                    value={formData.expenseProject}
                    onChange={handleChange}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                    disabled={isLoadingProjects}
                  >
                    <option value="">Select a Project</option>
                    {projects?.map((project) => (
                      <option 
                        key={project.proj_id} 
                        value={project.proj_id}
                        className="py-1"
                      >
                        {project.proj_desc} ({project.proj_id})
                      </option>
                    ))}
                  </select>
                </div>
                <p className="mt-1 text-xs text-gray-500">Associated project for this expense</p>
              </div>

              {/* Acquittal Date */}
              <div>
                <label htmlFor="acquisitionDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Acquittal Date
                </label>
                <input
                  type="date"
                  id="acquisitionDate"
                  name="acquisitionDate"
                  value={formData.acquisitionDate}
                  onChange={handleChange}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-red-500 focus:border-red-500"
                />
                <p className="mt-1 text-xs text-gray-500">Date of financial acquittal if applicable</p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3 sticky bottom-0 pt-4 bg-white">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Adding...' : 'Add Expense Record'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
